import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Send,
  Facebook,
  Twitter,
  Instagram,
  AccessTime,
} from '@mui/icons-material';
import { ContactForm } from '../types';
import HeroSection from './HeroSection';
import CustomSnackbar from './utils/CustomSnackbar';

export default function ContactPage() {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Here you would typically send the form data to a server
    setSnackbar({
      open: true,
      message: 'Thank you for contacting us! We will get back to you soon.',
      severity: 'success',
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <Phone />,
      title: 'Phone',
      details: ['+1 (555) 123-4567', '+1 (555) 987-6543'],
      color: '#d32f2f',
    },
    {
      icon: <Email />,
      title: 'Email',
      details: ['info@gudiyacooking.com', 'support@gudiyacooking.com'],
      color: '#1976d2',
    },
    {
      icon: <LocationOn />,
      title: 'Address',
      details: ['123 Food Street', 'Culinary City, CC 12345'],
      color: '#388e3c',
    },
    {
      icon: <AccessTime />,
      title: 'Hours',
      details: [
        'Mon-Fri: 11:00 AM - 10:00 PM',
        'Saturday: 10:00 AM - 11:00 PM',
        'Sunday: 10:00 AM - 9:00 PM',
      ],
      color: '#f57c00',
    },
  ];

  const faqs = [
    {
      question: 'Do you offer delivery services?',
      answer: 'Yes, we offer delivery within a 10-mile radius. Delivery is available during our restaurant hours.',
    },
    {
      question: 'Can I make reservations?',
      answer: 'Absolutely! You can make reservations by calling us or using our online booking system.',
    },
    {
      question: 'Do you cater for dietary restrictions?',
      answer: 'Yes, we offer vegetarian, vegan, and gluten-free options. Please inform us of any allergies when ordering.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept cash, credit/debit cards, and mobile payments. We also accept gift cards.',
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeroSection
        title="Contact Us"
        description="Get in touch with us for reservations, orders, or any questions"
      />
      <Container maxWidth="lg">
      

        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 6, borderRadius: 2 }}>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                Send us a Message
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Fill out the form below and we'll get back to you as soon as possible.
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<Send />}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 4 }}>
              {contactInfo.map((info, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: info.color,
                          width: 48,
                          height: 48,
                          mr: 2,
                        }}
                      >
                        {info.icon}
                      </Avatar>
                      <Typography variant="h6" fontWeight={600}>
                        {info.title}
                      </Typography>
                    </Box>
                    {info.details.map((detail, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 7, mb: 0.5 }}
                      >
                        {detail}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Social Media */}
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Follow Us
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <Facebook />
                </IconButton>
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <Twitter />
                </IconButton>
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <Instagram />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* FAQ Section */}
        <Paper sx={{ p: 6, mt: 8, borderRadius: 2 }}>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Quick answers to common questions
          </Typography>
          <Grid container spacing={3}>
            {faqs.map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {faq.question}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Map Placeholder */}
        <Paper sx={{ p: 6, mt: 8, borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Find Us
          </Typography>
          <Box
            sx={{
              height: 400,
              bgcolor: 'grey.200',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <LocationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Interactive Map
            </Typography>
            <Typography variant="body2" color="text.secondary">
              123 Food Street, Culinary City, CC 12345
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Success Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
        vertical="top"
        horizontal="right"
      />
    </Box>
  );
}
